"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import DOMPurify from "isomorphic-dompurify";
import { Box } from "@mui/material";
import { Text } from "@nxt-costco-com/forge-components";
import { ColorGray700 } from "@nxt-costco-com/forge-design-tokens";
import { EcoFee } from "../../../../../EcoFee/index.js";
import { generateDiscountText, calculateSavings, displayPromoBreakdown } from "../../../../../Price/lib.js";
import { PriceTooltip } from "../../../../../PriceTooltip/index.js";
import { getFormattedPrice } from "../../../../../ProductTile/ProductTileUI/CardBody/PriceGroup/Price/lib.js";
import { TaxableItem } from "../TaxableItem/index.js";
import { useProductTileData } from "../../../../../../../hooks/useProductTileData/index.js";
import { InstantSavings } from "./InstantSavings/index.js";
import { Price } from "./Price/index.js";
import { priceGroupContainerStyles, promoTextStyles, defaultPromoTextColor } from "./styles.js";
const COMPONENT_NAME = "PriceGroup";
const PriceGroup = ({
  productTileMaxLineNumber = 3,
  enablePromoBreakdown
}) => {
  const {
    isInformative,
    normalizedData,
    translationsConfig,
    locale,
    isSignedIn
  } = useProductTileData();
  const {
    discountsData,
    isMemberOnlyItem,
    priceListData,
    promoItem,
    productInfoData,
    savingsData,
    ecoFee
  } = normalizedData;
  const { promoText, promoTextColor } = promoItem || {};
  const { partNumber } = productInfoData;
  productTileMaxLineNumber = isInformative ? 3 : 2;
  let sanitizedPromoText = typeof window !== "undefined" ? DOMPurify.sanitize(promoText) : "";
  const { translations, promoBreakdownCAProvince } = translationsConfig || {};
  const instantSavingsText = (translations == null ? void 0 : translations.instantsavings) || "";
  const showPrice = priceListData && !isMemberOnlyItem || priceListData && isSignedIn;
  if (showPrice) {
    sanitizedPromoText = generateDiscountText(
      sanitizedPromoText,
      translations == null ? void 0 : translations.discountregex,
      translations == null ? void 0 : translations.afterprefix
    );
  }
  const promoTextColorStyles = {
    color: promoTextColor || defaultPromoTextColor
  };
  const savingsObj = calculateSavings(
    locale,
    savingsData == null ? void 0 : savingsData.listPrice,
    savingsData == null ? void 0 : savingsData.salePrice
  );
  let originalPrice = typeof (savingsData == null ? void 0 : savingsData.listPrice) === "string" ? parseFloat(savingsData == null ? void 0 : savingsData.listPrice) : savingsData == null ? void 0 : savingsData.listPrice;
  if (originalPrice && (ecoFee == null ? void 0 : ecoFee.includeInPrice)) {
    originalPrice += parseFloat(ecoFee == null ? void 0 : ecoFee.includeInPrice) || 0;
  }
  const formattedListPrice = getFormattedPrice(locale, originalPrice);
  const updatedPromoTextStyles = {
    ...promoTextColorStyles,
    ...promoTextStyles,
    WebkitLineClamp: productTileMaxLineNumber,
    lineClamp: productTileMaxLineNumber
  };
  return /* @__PURE__ */ jsxs(
    Box,
    {
      "data-testid": `${COMPONENT_NAME}_${partNumber}`,
      sx: priceGroupContainerStyles,
      children: [
        /* @__PURE__ */ jsx(TaxableItem, {}),
        (priceListData == null ? void 0 : priceListData.fulfillmentTexts) && /* @__PURE__ */ jsx(
          PriceTooltip,
          {
            id: partNumber,
            priceListData
          }
        ),
        showPrice && /* @__PURE__ */ jsx(
          Price,
          {
            ...priceListData,
            locale,
            uniqueId: partNumber
          }
        ),
        isInformative && /* @__PURE__ */ jsx(
          EcoFee,
          {
            locale,
            priceValue: priceListData == null ? void 0 : priceListData.priceValue
          }
        ),
        promoText && /* @__PURE__ */ jsx(
          Text,
          {
            sx: updatedPromoTextStyles,
            uniqueId: `promoText_${partNumber}`,
            variant: "t7",
            children: /* @__PURE__ */ jsx(
              "span",
              {
                dangerouslySetInnerHTML: {
                  __html: sanitizedPromoText
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          InstantSavings,
          {
            displayDiscount: discountsData == null ? void 0 : discountsData.displayDiscount,
            instantSavingsText,
            locale,
            partNumber,
            promoText: sanitizedPromoText,
            totalDiscount: discountsData == null ? void 0 : discountsData.totalDiscount
          }
        ),
        showPrice && displayPromoBreakdown(
          enablePromoBreakdown,
          priceListData,
          locale,
          promoBreakdownCAProvince,
          savingsObj == null ? void 0 : savingsObj.displaySavings
        ) && /* @__PURE__ */ jsxs(
          Text,
          {
            sx: { ...updatedPromoTextStyles, color: ColorGray700 },
            uniqueId: `listPrice_${partNumber}`,
            variant: "t6",
            children: [
              translations == null ? void 0 : translations.waspriceprefix,
              " ",
              formattedListPrice
            ]
          }
        )
      ]
    }
  );
};
export {
  PriceGroup
};
